import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tasksApi, importExportApi } from '../api';
import { Plus, Pencil, Trash2, CheckSquare, Upload, Download } from 'lucide-react';
import TaskModal from '../components/TaskModal';
import ImportModal from '../components/ImportModal';

export default function TasksPage() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [preset, setPreset] = useState<'current' | 'new'>('current');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async (overrides?: { preset?: 'current' | 'new'; page?: number }) => {
    const activePreset = overrides?.preset ?? preset;
    const activePage = overrides?.page ?? page;
    setLoading(true);
    try {
      const res = await tasksApi.list({
        search: search || undefined,
        page: activePage,
        limit,
        ...(activePreset === 'new'
          ? { sort: 'newest' }
          : { status: 'PENDING,IN_PROGRESS' }),
      });
      setTasks(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Load tasks error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, page, preset]);
  useEffect(() => { setPage(1); }, [search, preset]);

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить задачу?')) return;
    try { await tasksApi.remove(id); load(); } catch (e) { alert('Ошибка удаления'); }
  };

  const toggleComplete = async (task: any) => {
    try {
      await tasksApi.update(task.id, { isCompleted: !task.isCompleted });
      load();
    } catch (e) { alert('Ошибка обновления'); }
  };

  const handleExport = async () => {
    try {
      const res = await importExportApi.exportTasks();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const d = new Date();
      a.download = `tasks_${d.toISOString().slice(0, 10)}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка экспорта');
    }
  };

  const priorityLabel: Record<string, string> = { LOW: 'Низкий', MEDIUM: 'Средний', HIGH: 'Высокий', URGENT: 'Срочно' };
  const priorityColor: Record<string, string> = { LOW: 'badge-default', MEDIUM: 'badge-info', HIGH: 'badge-warning', URGENT: 'badge-danger' };
  const statusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
  const statusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Задачи</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={18} /> Импорт
          </button>
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={18} /> Экспорт
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
            <Plus size={18} /> Добавить задачу
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="toolbar" style={{ margin: 0, width: '100%', display: 'flex', gap: 12, alignItems: 'center' }}>
            <div className="btn-group">
              <button className={`btn btn-sm ${preset === 'new' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPreset('new')}>Новые задачи</button>
              <button className={`btn btn-sm ${preset === 'current' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPreset('current')}>Текущие задачи</button>
            </div>
            <input className="search-input" placeholder="Поиск по задачам..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div> : (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th></th><th>Название</th><th>Приоритет</th><th>Срок</th><th>Исполнитель</th><th>Статус</th>{preset === 'new' && <th>Создана</th>}<th>Действия</th></tr></thead>
                <tbody>
                  {tasks.length === 0 && <tr><td colSpan={preset === 'new' ? 8 : 7} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Задачи не найдены</td></tr>}
                  {tasks.map(t => (
                    <tr key={t.id} style={{ opacity: t.isCompleted ? 0.6 : 1 }}>
                      <td style={{ width: 40 }}>
                        <button onClick={() => toggleComplete(t)} className="header-icon-btn" style={{ width: 32, height: 32 }}>
                          <CheckSquare size={18} color={t.isCompleted ? 'var(--color-success)' : 'var(--color-text-muted)'} />
                        </button>
                      </td>
                      <td style={{ fontWeight: 600, textDecoration: t.isCompleted ? 'line-through' : 'none' }}><Link to={`/tasks/${t.id}`} className="detail-link">{t.title}</Link></td>
                      <td><span className={`badge ${priorityColor[t.priority] || 'badge-default'}`}>{priorityLabel[t.priority] || t.priority}</span></td>
                      <td className="text-muted">{t.dueDate ? new Date(t.dueDate).toLocaleDateString('ru-RU') : '—'}</td>
                      <td>{t.assignee?.firstName} {t.assignee?.lastName}</td>
                      <td><span className={`badge ${statusColor[t.status] || 'badge-default'}`}>{statusLabel[t.status] || t.status}</span></td>
                      {preset === 'new' && <td className="text-muted">{t.createdAt ? new Date(t.createdAt).toLocaleDateString('ru-RU') : '—'}</td>}
                      <td>
                        <div className="flex gap-2">
                          <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => { setEditing(t); setShowModal(true); }}>
                            <Pencil size={14} />
                          </button>
                          <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDelete(t.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-soft)' }}>
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
            <div className="flex gap-2" style={{ margin: '0 16px' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Страница {page}</span>
            </div>
            <button className="btn btn-secondary btn-sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Далее</button>
          </div>
        </div>
      </div>

      {showModal && (
        <TaskModal
          task={editing}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            if (!editing) {
              if (preset !== 'new' || page !== 1) {
                setPreset('new');
                setPage(1);
              } else {
                load();
              }
            } else {
              load();
            }
          }}
        />
      )}

      {showImport && (
        <ImportModal
          entityType="tasks"
          onImport={(file) => importExportApi.importTasks(file)}
          onClose={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}
